import { useCallback, useState } from 'react';
import {
  downloadPreparedValidooFile,
  isValidooBlockedResponse,
  prepareValidooExport,
} from './service';
import { ValidooExportErrorInfo, ValidooPrepareSuccessResponse } from './types';

const DOWNLOAD_DELAY_MS = 350;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getErrorMessage(error: any): string {
  return error?.response?.data?.message || error?.message || 'Validoo-filer kunde inte skapas.';
}

export function useValidooExport() {
  const [isSaving, setIsSaving] = useState(false);
  const [errorInfo, setErrorInfo] = useState<ValidooExportErrorInfo | null>(null);
  const [successResult, setSuccessResult] = useState<ValidooPrepareSuccessResponse | null>(null);

  const closeErrorDialog = useCallback(() => {
    setErrorInfo(null);
  }, []);

  const closeSuccessDialog = useCallback(() => {
    setSuccessResult(null);
  }, []);

  const saveForValidoo = useCallback(async (assetId: string) => {
    setIsSaving(true);
    setErrorInfo(null);
    setSuccessResult(null);

    try {
      const prepared = await prepareValidooExport(assetId);

      await downloadPreparedValidooFile(
        prepared.downloads.marketImage.downloadUrl,
        prepared.downloads.marketImage.filename
      );

      await delay(DOWNLOAD_DELAY_MS);

      await downloadPreparedValidooFile(
        prepared.downloads.planogramImage.downloadUrl,
        prepared.downloads.planogramImage.filename
      );

      setSuccessResult(prepared);
      return { ok: true as const };
    } catch (error: any) {
      if (isValidooBlockedResponse(error)) {
        setErrorInfo({ kind: 'blocked', data: error.response.data });
      } else {
        setErrorInfo({ kind: 'error', message: getErrorMessage(error) });
      }

      return { ok: false as const };
    } finally {
      setIsSaving(false);
    }
  }, []);

  return {
    isSaving,
    errorInfo,
    successResult,
    closeErrorDialog,
    closeSuccessDialog,
    saveForValidoo,
  };
}
